import * as React from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import AppTheme from '../shared-theme/AppTheme';
import SitemarkIcon from '../marketing-page/components/SitemarkIcon';
import SignUpForm from './SignUpForm';
import ChooseRoleStep from './ChooseRoleStep';
import { HandymanAppState } from '../../configuration/redux/reducers';
import { SignInErrorType } from '../../pages/Auth/reducer';
import { useNavigate } from 'react-router-dom';
import { CommunicationChannel, RegisterUserCommand } from '../../configuration/api/apiSchema';
import { connect } from 'react-redux';
import { checkDuplicateEmail, sendSignUpUser, sendSignUpUserOtp, signInUser, UserRoles } from '../../pages/Auth/actions';
import EnterOtpCodeStep from './EnterOtpCodeStep';
import { LoadScript } from '@react-google-maps/api';


interface DispatchFromProps {
  sendSignUpUserOtp: (otpContactReference: string, otpCommunicationChannel: CommunicationChannel) => void;
  sendSignUpUser: (command: RegisterUserCommand) => void;
  signInUser: (username: string, password: string) => void;
  checkDuplicateEmail: (email: string) => void;
}

interface StateFromProps {
  isError: boolean;
  isLoading: boolean;
  errorType?: SignInErrorType;
  isSignUpSuccessfull: boolean;
  isOtpConfirmed: boolean;
  isAuthorized: boolean;
  isSignupLoading: boolean;
  isDuplicatedEmailError: boolean;
  isDuplicatedEmailChecked: boolean;
}

interface PassedProps {
  disableCustomTheme?: boolean
}

enum SignUpSteps {
  ChooseRole = 1,
  SignUpForm = 2,
  ConfirmOtpCode = 3
}

export type UserRoleType = 'handyman' | 'customer';

const SignUpFormWizzard = (props: PassedProps & DispatchFromProps & StateFromProps) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = React.useState<SignUpSteps>(SignUpSteps.ChooseRole);
  const [userRoleId, setUserRoleId] = React.useState<UserRoles>(UserRoles.CustomerRoleId);
  const [user, setUser] = React.useState<RegisterUserCommand>({});
  const [selectedRole, setSelectedRole] = React.useState<'handyman' | 'customer'>('customer');


  React.useEffect(() => {
    if (props.isSignUpSuccessfull) {
      props.signInUser(user.email as string, user.password as string);
    }
  }, [props.isSignUpSuccessfull])

  React.useEffect(() => {
    if (props.isAuthorized) {
      navigate("/profile");
    }
  }, [props.isAuthorized])

  React.useEffect(() => {
    if (props.isDuplicatedEmailChecked && !props.isDuplicatedEmailError) {
      props.sendSignUpUserOtp(user.email as string, CommunicationChannel.Email);

      setCurrentStep(SignUpSteps.ConfirmOtpCode)
    }
  }, [props.isDuplicatedEmailChecked])

  const onRoleSelect = (role: UserRoleType) => {

    setSelectedRole(role);

    if (role === 'handyman') {
      setUserRoleId(UserRoles.HandymanRoleId)
    }
    else if (role === 'customer') {
      setUserRoleId(UserRoles.CustomerRoleId)
    }

    setCurrentStep(SignUpSteps.SignUpForm)
  }

  const onFormSubmit = (email: string, password: string, name: string, lastname: string) => {
    props.checkDuplicateEmail(email);

    setUser({
      email: email,
      password: password,
      firstName: name,
      lastName: lastname,
      otpCommunicationChannel: CommunicationChannel.Email,
      otpContactReference: email,
      roleId: userRoleId
    });
  }

  const onOtpCodeSubmit = (otpCode: string) => {
    props.sendSignUpUser({
      ...user,
      otpCode: otpCode
    });
  }

  return (
    <LoadScript
      googleMapsApiKey={'AIzaSyCkpt0UTpGCtRER3R4GsdZIJ3zNiHtQm00'}
      libraries={['places']}
    >
      <AppTheme>
        <CssBaseline enableColorScheme />
        <SignUpContainer direction="column" justifyContent="space-between">
          <Card variant="outlined">
            <SitemarkIcon />
  
            {currentStep === SignUpSteps.ChooseRole && <ChooseRoleStep onSelect={onRoleSelect} />}
  
            {currentStep === SignUpSteps.SignUpForm && (
              <SignUpForm
                onSubmit={onFormSubmit}
                isLoading={props.isLoading}
                isDuplicatedEmailError={props.isDuplicatedEmailError}
                selectedRole={selectedRole}
              />
            )}
  
            {currentStep === SignUpSteps.ConfirmOtpCode && (
              <EnterOtpCodeStep
                onSubmit={onOtpCodeSubmit}
                email={user.email as string}
                isLoading={props.isLoading}
                errorType={props.errorType}
              />
            )}
          </Card>
        </SignUpContainer>
      </AppTheme>
    </LoadScript>
  );
}

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignUpContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100%',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

const mapStateToProps = (state: HandymanAppState): StateFromProps => {
  return {
    isError: state.user.isError,
    isLoading: state.user.isLoading,
    errorType: state.user.errorType,
    isSignUpSuccessfull: state.user.isSignUpSuccessfull,
    isOtpConfirmed: state.user.isOtpConfirmed,
    isAuthorized: state.user.isAuthorized,
    isSignupLoading: state.user.isSignupLoading,
    isDuplicatedEmailError: state.user.isDuplicatedEmailError,
    isDuplicatedEmailChecked: state.user.isDuplicatedEmailChecked,
  };
};

export default connect<
  StateFromProps,
  DispatchFromProps,
  any,
  HandymanAppState
>(mapStateToProps, {
  sendSignUpUserOtp: sendSignUpUserOtp,
  sendSignUpUser: sendSignUpUser,
  signInUser: signInUser,
  checkDuplicateEmail: checkDuplicateEmail
})(SignUpFormWizzard);